import { KeyboardEvent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	ButtonProps,
	Image as ChakraImage,
	Checkbox,
	Flex,
	FormControl,
	FormErrorMessage,
	IconButton,
	Input,
	PlusOutline,
	rem,
	Stack,
	Text,
	TrashOutline,
} from '@bonusly/design-system';

import { FormLabel } from '../../form/form-label';
import { Image } from '../types/announcements';

// 1MB is 1,048,576 bytes
const MAX_FILE_SIZE = 10 * 1048576; // 10MB

interface ImageUploadFieldProps {
	image?: Image;
	setRemoveImage?: (removeImage: boolean) => void;
}

export function ImageUploadField({ image, setRemoveImage }: ImageUploadFieldProps) {
	const { t } = useTranslation('announcements', { keyPrefix: 'modal' });

	const {
		register,
		setValue,
		formState: { errors },
		watch,
	} = useFormContext();

	// NOTE: when editing we wont have the file, we will only have the image name and url
	// so we need a state to keep track of the image name that defaults to what was passed in
	// and is updated by the image upload field
	const [imageName, setImageName] = useState<string | undefined>(image?.filename);

	const files = watch('image');
	const file = files && files[0];

	const [previewUrl, setPreviewUrl] = useState<string | undefined>(() => (image?.url && image?.filename ? image.url : undefined));

	useEffect(() => {
		if (!file) {
			setPreviewUrl(image?.url && imageName ? image.url : undefined);
			return;
		}
		const url = URL.createObjectURL(file);
		setPreviewUrl(url);
		return () => URL.revokeObjectURL(url);
	}, [file, image?.url, imageName]);

	useEffect(() => {
		if (file) {
			setImageName(file.name);
			setRemoveImage && setRemoveImage(false);
		}
	}, [file, setRemoveImage]);

	const handleClickOrKeyDown = useCallback((e: KeyboardEvent | MouseEvent) => {
		if (e.type === 'click' || (e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
			document.getElementById('image-uploader')?.click();
		}
	}, []);

	const handleDeleteImage = useCallback(() => {
		setValue('image', undefined);
		setImageName(undefined);
		// NOTE: we need to set removeImage to true so that the backend knows to remove the image
		if (image?.filename && setRemoveImage) {
			setRemoveImage(true);
		}
	}, [setValue, image?.filename, setRemoveImage]);

	return (
		<FormControl data-testid="image-upload-field" isInvalid={!!errors.image}>
			<FormLabel fontSize={50} lineHeight={5} id="image-attachment" isOptional>
				{t('image_upload_label')}
			</FormLabel>

			{imageName ? (
				<Flex
					h="fit-content"
					p={3}
					w="100%"
					alignItems="center"
					justifyContent="space-between"
					borderColor="border.input"
					borderRadius="md"
					borderWidth={rem(1)}
					backgroundColor="bg.surface"
					color="text.default"
				>
					<Flex alignItems="center" gap={3} overflow="hidden">
						<Box flexShrink={0} w={rem(64)} h={rem(64)} borderRadius="sm" overflow="hidden">
							<ChakraImage src={previewUrl} alt={imageName} w="100%" h="100%" objectFit="cover" />
						</Box>
						<Checkbox {...register('full_width_image')}>
							<Text fontSize={50} fontWeight="normal" lineHeight={5}>
								{t('full_width_image_label')}
							</Text>
						</Checkbox>
					</Flex>
					<IconButton
						aria-label={t('delete_image')}
						data-testid="delete-image"
						onClick={handleDeleteImage}
						colorScheme="secondary"
						variant="ghost"
						flexShrink={0}
						_hover={{
							bgColor: 'secondary.bgAlpha',
						}}
					>
						<TrashOutline />
					</IconButton>
				</Flex>
			) : (
				<>
					<StyledButton
						aria-label={t('image_upload_action')}
						aria-labelledby="image-attachment"
						onClick={handleClickOrKeyDown}
						onKeyDown={handleClickOrKeyDown}
						borderColor={errors?.image ? 'error.inputBorder' : 'border.input'}
						borderWidth={errors?.image ? rem(2) : rem(1)}
					>
						<Flex alignItems="center">
							<PlusOutline color="text.default" size="sm" />
							<Stack ml={4} spacing={0} textAlign="left">
								<Text color="text.default" fontSize={50} fontWeight="medium">
									{t('image_upload_action')}
								</Text>
								<Text color="text.softer" fontSize={50} fontWeight="normal">
									{t('image_upload_description')}
								</Text>
							</Stack>
						</Flex>
					</StyledButton>
					<Input
						id="image-uploader"
						data-testid="image-uploader"
						accept="image/jpeg, image/jpg, image/png"
						display="none"
						type="file"
						{...register('image', {
							validate: {
								fileSize: (file) => {
									if (file && file[0] && file[0].size > MAX_FILE_SIZE) {
										return t('messages.image_upload_too_big');
									}
									return true;
								},
							},
						})}
					/>
				</>
			)}
			<FormErrorMessage>{typeof errors?.image?.message === 'string' ? errors.image.message : ''}</FormErrorMessage>
		</FormControl>
	);
}

const StyledButton = (props: ButtonProps) => {
	return (
		<Button
			h="fit-content"
			p={4}
			w="100%"
			justifyContent="left"
			borderColor="border.input"
			borderRadius="md"
			borderWidth={rem(1)}
			backgroundColor="bg.surface"
			color="text.default"
			_active={{
				borderColor: 'border.input',
				borderRadius: 'md',
				borderWidth: rem(1),
				backgroundColor: 'bg.surface',
				color: 'text.default',
			}}
			_disabled={{
				cursor: 'not-allowed',
				opacity: 0.6,
			}}
			_hover={{
				borderColor: 'border.input',
				borderRadius: 'md',
				borderWidth: rem(1),
				backgroundColor: 'bg.surface',
				color: 'text.default',
			}}
			{...props}
		/>
	);
};
